import { useState } from 'react';
import { FileText } from 'lucide-react';
import { apiDownload } from '../../../lib/api';
import { InfoTooltip } from '../InfoTooltip';
import {
  CARBON_CATEGORY_META,
  CARBON_CATEGORY_ORDER,
  type CarbonCategoryKey,
} from './carbonCategories';
import {
  CARBON_CONSTANT_GRID_CAVEAT,
  CARBON_FRAMING_LINE,
  CARBON_METHOD_BADGE,
  CARBON_METHOD_NOTE,
  CARBON_UNLOCATED_ASSETS_NOTE,
} from './carbonCopy';

/**
 * SIL-440 — the disclosure furniture the Variant-A forecast card carries
 * (spec §6, copy §1-§5). Split out of the card itself so a legal re-vet
 * reviews one small file, and so the trajectory dashboard (a later ticket)
 * reuses these rather than re-typing the strings.
 */

/** Copy §2 — the method badge, with the market-based note as its tooltip. */
export function CarbonMethodBadge() {
  return (
    <span className="inline-flex items-center gap-1 rounded bg-white/70 px-1.5 py-0.5 text-xs font-semibold uppercase tracking-wide text-violet-800 ring-1 ring-inset ring-violet-200">
      {CARBON_METHOD_BADGE}
      <InfoTooltip label={`About the ${CARBON_METHOD_BADGE.toLowerCase()}`}>
        {CARBON_METHOD_NOTE}
      </InfoTooltip>
    </span>
  );
}

/** Copy §4 — one chip per GHG category, colour-matched to the chart stack.
 * A category with a §4 helper (Cat 8 colocation, Cat 1/2 fixed-asset
 * treatment, Cat 5 timing) carries it in a tooltip on the chip itself. */
export function CarbonCategoryChips() {
  return (
    <div className="flex flex-wrap gap-1.5">
      {CARBON_CATEGORY_ORDER.map((key) => (
        <CarbonCategoryChip key={key} categoryKey={key} />
      ))}
    </div>
  );
}

function CarbonCategoryChip({ categoryKey }: { categoryKey: CarbonCategoryKey }) {
  const meta = CARBON_CATEGORY_META[categoryKey];
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-violet-200 bg-white px-2 py-0.5 text-xs text-zinc-600">
      <span
        aria-hidden="true"
        className="size-2 shrink-0 rounded-full"
        style={{ backgroundColor: meta.color }}
      />
      {meta.label}
      {meta.helper && (
        <InfoTooltip label={`About ${meta.label}`}>{meta.helper}</InfoTooltip>
      )}
    </span>
  );
}

/**
 * Copy §14 — the methodology & assumptions sheet. Downloads the
 * auto-generated file the ESG export ships as its third companion
 * (`GET /api/client/scenarios/:id/export/esg/methodology.csv`, SIL-438);
 * this is a TRIGGER for that same artifact, never a second, hand-written
 * summary that could drift from it.
 */
export function MethodologySheetTrigger({ scenarioId }: { scenarioId: string }) {
  const [downloading, setDownloading] = useState(false);
  const [failed, setFailed] = useState(false);

  const handleDownload = async () => {
    setDownloading(true);
    setFailed(false);
    try {
      await apiDownload(
        `/api/client/scenarios/${scenarioId}/export/esg/methodology.csv`,
        'esg-methodology.csv',
      );
    } catch (err) {
      console.error('[carbon-methodology-sheet] download failed:', err);
      setFailed(true);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={() => void handleDownload()}
      disabled={downloading}
      className="inline-flex items-center gap-1.5 text-xs font-medium text-violet-700 hover:underline disabled:cursor-not-allowed disabled:opacity-50"
    >
      <FileText aria-hidden="true" className="size-3.5" />
      {downloading
        ? 'Preparing…'
        : failed
          ? 'Methodology sheet failed — retry'
          : 'Methodology & assumptions sheet'}
    </button>
  );
}

/**
 * Copy §1 + §3 + §5 — the disclosure footnotes every carbon chart carries.
 * Rendered as plain text under the chart, never behind a tooltip: a
 * screenshot or a printed page must still carry the framing line.
 */
export function CarbonForecastFootnotes() {
  return (
    <div className="mt-3 space-y-1 text-xs leading-relaxed text-zinc-500">
      <p className="font-medium text-zinc-600">{CARBON_FRAMING_LINE}</p>
      <p>{CARBON_CONSTANT_GRID_CAVEAT}</p>
      <p>{CARBON_UNLOCATED_ASSETS_NOTE}</p>
    </div>
  );
}
